import { useCallback, useState } from 'react';
import { isEqual } from 'lodash-es';
import { useTranslation } from '@frontend/i18n';
import { BannerNotification, Modal, ModalControlModalProps, Text } from '@frontend/design-system';
import { ConenctProviderFormValues, ConnectProviderForm } from './connect-provider-form';

export type ConnectProviderModalProps = {
  modalProps: ModalControlModalProps;
  isMutating: boolean;
  errorMessage: string | null;
  onSubmit: (values: ConenctProviderFormValues) => Promise<void>;
};

export const ConnectProviderModal = ({ modalProps, errorMessage, isMutating, onSubmit }: ConnectProviderModalProps) => {
  const { t } = useTranslation('payments');
  const [isComplete, setIsComplete] = useState(false);
  const [formValues, setFormValues] = useState<ConenctProviderFormValues>({
    merchantNumber: '',
    validationMethod: '',
    validationCode: '',
  });

  const onChangeFormState = useCallback(
    (values: ConenctProviderFormValues, isCompleteNew: boolean) => {
      if (isCompleteNew !== isComplete) {
        setIsComplete(isCompleteNew);
      }
      if (!isEqual(values, formValues)) {
        setFormValues(values);
      }
    },
    [isComplete, formValues]
  );

  return (
    <Modal {...modalProps} maxWidth={600} minWidth={600}>
      <Modal.Header onClose={modalProps.onClose}>{t('Connect Account')}</Modal.Header>
      <Modal.Body>
        <Text>{t('Enter your CareCredit Merchant ID Number below to connect your account.')}</Text>

        <ConnectProviderForm onSubmit={onSubmit} onChangeFormState={onChangeFormState} />

        {errorMessage && errorMessage.length > 0 && (
          <BannerNotification status='error'>{errorMessage}</BannerNotification>
        )}
      </Modal.Body>
      <Modal.Footer
        primary={{
          loading: isMutating,
          disabled: !isComplete || isMutating,
          label: t('Validate'),
          onClick: () => onSubmit(formValues),
        }}
        secondary={{
          disabled: isMutating,
          label: t('Cancel'),
          onClick: modalProps.onClose,
        }}
      />
    </Modal>
  );
};
